<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.cfg_categories') }}
      </oxd-text>
      <oxd-button
        v-if="canWrite"
        display-type="secondary"
        :label="$t('general.add')"
        @click="onAdd"
      />
    </div>
    <oxd-divider />
    <oxd-text
      v-if="state === 'loading'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.categories_empty') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="rows"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
  </div>

  <teleport to="#app">
    <oxd-dialog
      v-if="showForm"
      class="orangehrm-dialog-modal"
      @update:show="showForm = false"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">
          {{
            editing ? $t('expensa.edit_category') : $t('expensa.new_category')
          }}
        </oxd-text>
      </div>
      <oxd-divider />
      <oxd-form :loading="saving" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="form.code"
            :label="$t('expensa.col_code')"
            :rules="rules.code"
            :disabled="!!editing"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="form.name"
            :label="$t('general.name')"
            :rules="rules.name"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.glAccount"
                :label="$t('expensa.col_gl')"
                :rules="rules.glAccount"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="vatOption"
                type="select"
                :label="$t('expensa.col_vat_default')"
                :options="vatOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.vatDeductiblePct"
                :label="$t('expensa.col_vat_deductible')"
                :rules="rules.vatDeductiblePct"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.sortOrder"
                :label="$t('expensa.col_sort')"
                :rules="rules.sortOrder"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.requiresReceiptAbove"
                :label="$t('expensa.cat_receipt_above')"
                :rules="rules.money"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.maxPerItem"
                :label="$t('expensa.cat_max_item')"
                :rules="rules.money"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.maxPerDay"
                :label="$t('expensa.cat_max_day')"
                :rules="rules.money"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="form.keywords"
            :label="$t('expensa.cat_keywords')"
            :rules="rules.keywords"
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="form.active"
            type="checkbox"
            :true-value="true"
            :false-value="false"
            :option-label="$t('general.active')"
          />
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="showForm = false"
          />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.save')"
            :disabled="saving"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {expensaApi, errorMessage} from '../../util/api';
import {
  VAT_RATES,
  categoryPatch,
  categoryToForm,
  categoryValues,
  emptyCategoryForm,
  parseKeywords,
} from '../../util/configForms';
import {eur} from '../../util/format';

// Categorie di spesa: elenco (anche disattivate) e dialog di creazione/modifica.
export default {
  name: 'ExpensaCategoriesSection',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    canWrite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: 'loading',
      categories: [],
      showForm: false,
      editing: null,
      form: emptyCategoryForm(),
      saving: false,
    };
  },
  computed: {
    rows() {
      return this.categories.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.name,
        glAccount: c.glAccount || '—',
        vat: c.defaultVatRate === null ? '—' : `${c.defaultVatRate}%`,
        deductible: `${c.vatDeductiblePct}%`,
        limits: this.limitsText(c),
        active: c.active ? this.$t('general.yes') : this.$t('general.no'),
        sortOrder: String(c.sortOrder),
      }));
    },
    headers() {
      const headers = [
        {name: 'code', title: this.$t('expensa.col_code'), style: {flex: 2}},
        {name: 'name', title: this.$t('general.name'), style: {flex: 3}},
        {name: 'glAccount', title: this.$t('expensa.col_gl'), style: {flex: 1}},
        {
          name: 'vat',
          title: this.$t('expensa.col_vat_default'),
          style: {flex: 1},
        },
        {
          name: 'deductible',
          title: this.$t('expensa.col_vat_deductible'),
          style: {flex: 1},
        },
        {
          name: 'limits',
          title: this.$t('expensa.col_limits'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 3},
        },
        {name: 'active', title: this.$t('general.active'), style: {flex: 1}},
        {
          name: 'sortOrder',
          title: this.$t('expensa.col_sort'),
          style: {flex: 1},
        },
      ];
      if (this.canWrite) {
        headers.push({
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            edit: {onClick: this.onEdit, props: {name: 'pencil-fill'}},
          },
        });
      }
      return headers;
    },
    vatOptions() {
      return [
        {id: null, label: '—'},
        ...VAT_RATES.map((r) => ({id: r, label: `${r}%`})),
      ];
    },
    // Il select lavora su {id, label}; il form tiene solo l'aliquota.
    vatOption: {
      get() {
        return this.vatOptions.find((o) => o.id === this.form.defaultVatRate);
      },
      set(option) {
        this.form.defaultVatRate = option ? option.id : null;
      },
    },
    rules() {
      return {
        code: [
          required,
          (v) =>
            /^[A-Z][A-Z0-9_]{1,39}$/.test((v ?? '').trim()) ||
            this.$t('expensa.cat_invalid_code'),
        ],
        name: [required, shouldNotExceedCharLength(100)],
        glAccount: [shouldNotExceedCharLength(20)],
        vatDeductiblePct: [
          required,
          (v) =>
            /^\d{1,3}$/.test((v ?? '').trim()) && Number(v) <= 100
              ? true
              : this.$t('expensa.set_invalid_int'),
        ],
        sortOrder: [
          required,
          (v) =>
            /^\d{1,4}$/.test((v ?? '').trim())
              ? true
              : this.$t('expensa.set_invalid_int'),
        ],
        money: [(v) => this.moneyRule(v)],
        keywords: [
          (v) =>
            parseKeywords(v ?? '').ok ||
            this.$t('expensa.cat_invalid_keywords'),
        ],
      };
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    moneyRule(v) {
      const text = (v ?? '').trim();
      return (
        text === '' ||
        /^\d{1,8}(?:[.,]\d{1,2})?$/.test(text) ||
        this.$t('expensa.invalid_amount')
      );
    },
    limitsText(c) {
      const parts = [];
      if (c.requiresReceiptAbove) {
        parts.push(
          `${this.$t('expensa.cat_receipt_above')} ${eur(
            c.requiresReceiptAbove,
          )}`,
        );
      }
      if (c.maxPerItem) {
        parts.push(`${this.$t('expensa.cat_max_item')} ${eur(c.maxPerItem)}`);
      }
      if (c.maxPerDay) {
        parts.push(`${this.$t('expensa.cat_max_day')} ${eur(c.maxPerDay)}`);
      }
      return parts.length ? parts.join(' - ') : '—';
    },
    async load() {
      try {
        const {data} = await expensaApi('/categories').getAll({
          includeInactive: true,
        });
        this.categories = data.data;
        this.state = this.categories.length === 0 ? 'empty' : 'list';
      } catch (e) {
        this.state = 'error';
      }
    },
    onAdd() {
      this.editing = null;
      this.form = emptyCategoryForm();
      this.showForm = true;
    },
    onEdit(item) {
      const category = this.categories.find((c) => c.id === item.id);
      if (!category) return;
      this.editing = category;
      this.form = categoryToForm(category);
      this.showForm = true;
    },
    async onSave() {
      this.saving = true;
      try {
        if (this.editing) {
          const {patch, invalid} = categoryPatch(this.editing, this.form);
          if (invalid.length > 0) throw new Error(invalid.join(','));
          if (Object.keys(patch).length > 0) {
            await expensaApi('/categories').update(this.editing.id, patch);
          }
        } else {
          const {values, invalid} = categoryValues(this.form);
          if (invalid.length > 0) throw new Error(invalid.join(','));
          const body = {};
          Object.keys(values).forEach((key) => {
            if (values[key] !== null) body[key] = values[key];
          });
          await expensaApi('/categories').create(body);
        }
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_done'),
        });
        this.showForm = false;
        await this.load();
      } catch (e) {
        // Il messaggio del server (per esempio MILEAGE non si disattiva) arriva così com'è.
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
